import React from 'react';
import { Button } from '@mui/material';
import { COLORS } from '../../Pages/admin/shared/tokens';
import { approvalStatus, approvalActions } from '../../helper';

/* Bulk approval actions for a set of selected rows. Added 2026-10-13.
 *
 * The buttons are derived from the SAME approvalActions() helper the per-row buttons use, so the
 * two can never offer different transitions — if the matrix changes, both change together.
 *
 * Mixed statuses are the normal case: a user ticks ten rows and some are pending while others
 * are already requested. Rather than refuse the lot, each action is offered with the number of
 * selected rows it actually applies to ("Approve 4"), and only those ids are sent. Rows the
 * action cannot apply to are simply not included — the server would skip them anyway, but
 * saying so up front is less confusing than a skipped-count afterwards.
 *
 * Props
 *   rows        selected rows (the raw records, so we can read each one's status)
 *   statusField name of the status field on a row, e.g. 'PC_WS_STATUS'
 *   idField     name of the id field, e.g. 'PC_WS_ID'
 *   canApprove  does this user hold APPROVE on this screen (affordance only — the endpoint
 *               re-checks it against pc_role_rights from the signed token)
 *   onAction    (to, ids) => void
 *   onClear     () => void
 *   busy        disables everything while a request is in flight
 *   excludeTargets
 *               transitions this screen must NOT offer in bulk, e.g. ['APPROVED'].
 *
 *               Weekly Sale passes ['APPROVED'] (decision 2026-10-13). Approving a weekly sale
 *               does more than set a status: it posts the partner's share of the week's profit
 *               via linking/WeeklySale.php, and that figure is computed in the open form from
 *               sales minus expenses. The list rows carry no expenses, and the stored
 *               WS_PRO_LOS column is unusable (NULL or 0 in every row), so a bulk approve would
 *               post every share as zero. Approving therefore stays one record at a time, from
 *               the form. Send-for-approval, Reject and Reopen are pure status changes and are
 *               offered normally.
 */
export default function BulkActionBar({ rows, statusField, idField, canApprove, onAction, onClear, busy, excludeTargets }) {
    const excluded = Array.isArray(excludeTargets) ? excludeTargets : [];
    const selected = Array.isArray(rows) ? rows : [];
    if (selected.length === 0) return null;

    // For each target status, which of the selected rows may move there.
    const buckets = {};
    selected.forEach(row => {
        approvalActions(row[statusField], canApprove).forEach(act => {
            if (!buckets[act.to]) buckets[act.to] = { label: act.label, colour: act.colour, ids: [] };
            buckets[act.to].ids.push(String(row[idField]));
        });
    });

    const actions = Object.keys(buckets)
        .map(to => ({ to, ...buckets[to] }))
        .filter(a => a.ids.length > 0 && !excluded.includes(a.to));

    // Why nothing is on offer, when that is the case — more useful than an empty bar.
    const allApproved = selected.every(r => approvalStatus(r[statusField]) === 'APPROVED');
    // When the only thing on offer was excluded, say so rather than showing an empty bar.
    const onlyExcluded = actions.length === 0
        && Object.keys(buckets).some(to => excluded.includes(to));
    const note = actions.length > 0 ? null
        : onlyExcluded
            ? 'Approve these one at a time — approving also posts the partner share, which is calculated on the record itself.'
        : canApprove
            ? 'Nothing can be changed in this selection.'
            : allApproved
                ? 'These are approved. You do not have permission to reopen them.'
                : 'You do not have permission to approve or reject these.';

    return (
        <div style={{
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px',
            margin: '0 16px 12px', padding: '10px 14px',
            background: COLORS.primaryTintSoft,
            border: `1px solid ${COLORS.border}`,
            borderRadius: '8px',
            fontFamily: "'Inter', sans-serif", fontSize: '13px',
        }}>
            <strong style={{ color: COLORS.text }}>
                {selected.length} selected
            </strong>

            {note && <span style={{ color: COLORS.textDim }}>{note}</span>}

            <span style={{ flex: 1 }} />

            {actions.map(act => (
                <Button
                    key={act.to}
                    size="small"
                    variant="contained"
                    color={act.colour}
                    disabled={busy}
                    onClick={() => onAction(act.to, act.ids)}
                    /* The count is on the button because it is NOT always the selection size:
                       "Approve 4" on a selection of 10 means six of them are not awaiting
                       approval. Seeing that before clicking beats a skipped list after. */
                    title={act.ids.length === selected.length
                        ? `${act.label} all ${selected.length} selected`
                        : `${act.label} the ${act.ids.length} of ${selected.length} selected that allow it`}
                >
                    {act.label}{act.ids.length === selected.length ? '' : ` ${act.ids.length}`}
                </Button>
            ))}

            <Button size="small" variant="outlined" disabled={busy} onClick={onClear}>
                Clear
            </Button>
        </div>
    );
}
